import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { getQuestionnaireDisplayTitle } from "@/lib/questionnaireDisplayTitle";
import { Tier1ClientCards } from "@/components/tier/Tier1ClientCards";
import { TierGate } from "@/components/tier/TierGate";
import { TIER1_MSP_CATEGORY } from "@/lib/tier";
import { BrandedLogo } from "@/components/ui/BrandedLogo";
import { getLogoUrl } from "@/utils/getLogoUrl";
import { LogoUpload, uploadLogoForEntity } from "@/components/admin/LogoUpload";
import type { LogoPathArgs } from "@/lib/slugify";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Building2, Loader2 } from "lucide-react";

/**
 * Tier 1 (MSP) Portfolio Manager Dashboard.
 *
 * Lightweight, client-card based experience:
 *  - Load/upload assessments
 *  - Delete assessments (via Tier1ClientCards)
 *  - 3-client limit
 *  - No CSV / NIST / CAP / Findings rollups
 *  - No pm_get_assessment_rollups usage
 *  - Locked TierGate hints for upgrade features
 */

interface SubOrgContext {
  sub_org_id: string;
  sub_org_name: string | null;
  client_name: string | null;
  client_logo: string | null;
  sub_org_logo: string | null;
}

interface CompanyRow {
  portfolio_id: string;
  assessment_id: string | null;
  name: string;
  logo_url: string | null;
  questionnaire_id: string | null;
  rating: string;
  score: number | null;
  respondent_score: number | null;
  reviewer_score: number | null;
  completion_pct: number;
  assessment_status: string;
  current_cycle: number;
  last_updated: string | null;
  hrv_composite_score: number | null;
  hrv_exposure_label: string | null;
  hrv_escalation_required: boolean;
  hrv_domain_scores: Array<{ domain: string; score: number; exposure_label: string }> | null;
}

interface Tier1DashboardData {
  companies: CompanyRow[];
}

interface QuestionnaireRow {
  questionnaire_key: string;
  q: { id: string; meta: unknown } | null;
}

const MAX_TIER1_CLIENTS = 3;

export default function Tier1Dashboard() {
  useEffect(() => {
    console.log("Rendering Tier1Dashboard (build: tier-split-2026-04-30)");
  }, []);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [selectedSubOrgId, setSelectedSubOrgId] = useState<string | null>(null);
  const [hasSubOrg, setHasSubOrg] = useState(true);
  const [orgContext, setOrgContext] = useState<{
    clientName: string | null;
    subOrgName: string | null;
    clientLogo: string | null;
    subOrgLogo: string | null;
  }>({ clientName: null, subOrgName: null, clientLogo: null, subOrgLogo: null });

  const [data, setData] = useState<Tier1DashboardData | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);

  // Create modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState("");
  const [availableAssessments, setAvailableAssessments] = useState<QuestionnaireRow[]>([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  // Load PM sub-org context
  useEffect(() => {
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate("/login", { replace: true });
        return;
      }
      const { data: contextData, error } = await (supabase as any).rpc("pm_my_sub_org_contexts");
      if (error) {
        console.error("[Tier1] pm_my_sub_org_contexts error:", error);
      } else if (contextData && contextData.length > 0) {
        const first = contextData[0] as SubOrgContext;
        setOrgContext({
          clientName: first.client_name,
          subOrgName: first.sub_org_name,
          clientLogo: first.client_logo,
          subOrgLogo: first.sub_org_logo,
        });
        setSelectedSubOrgId(first.sub_org_id);
      } else {
        setHasSubOrg(false);
      }
      setLoading(false);
    })();
  }, [navigate]);

  const loadDashboard = async (subOrgId: string) => {
    setLoadingDashboard(true);
    const { data: dashData, error } = await (supabase.rpc as any)(
      "pm_get_portfolio_dashboard",
      { p_sub_org_id: subOrgId },
    );
    if (error) {
      console.error("[Tier1] pm_get_portfolio_dashboard error:", error);
    } else {
      setData(dashData as Tier1DashboardData);
    }
    setLoadingDashboard(false);
  };

  useEffect(() => {
    if (selectedSubOrgId) loadDashboard(selectedSubOrgId);
  }, [selectedSubOrgId]);

  const refresh = async () => {
    if (selectedSubOrgId) await loadDashboard(selectedSubOrgId);
  };

  const openCreateModal = async () => {
    setShowCreateModal(true);
    if (availableAssessments.length === 0) {
      setLoadingAssessments(true);
      const { data: qRows, error } = await (supabase.rpc as any)(
        "pm_list_questionnaires_by_category",
        { p_category: TIER1_MSP_CATEGORY },
      );
      if (error || !qRows) {
        console.error("[Tier1] pm_list_questionnaires_by_category error:", error);
        setLoadingAssessments(false);
        return;
      }
      const merged = (qRows as any[]).map((q: any) => ({
        questionnaire_key: q.id,
        q: { id: q.id, meta: q.meta },
      }));
      setAvailableAssessments(merged);
      if (merged[0]) setSelectedQuestionnaire(merged[0].questionnaire_key);
      setLoadingAssessments(false);
    }
  };

  const handleCreate = async () => {
    if (!createName.trim() || !selectedQuestionnaire || !selectedSubOrgId) {
      toast.error("Please fill in all fields");
      return;
    }
    setCreating(true);
    const { data: rpcData, error } = await (supabase.rpc as any)("pm_create_portfolio", {
      p_sub_org_id: selectedSubOrgId,
      p_name: createName.trim(),
      p_category: TIER1_MSP_CATEGORY,
      p_questionnaire_id: selectedQuestionnaire,
    });
    if (error) {
      console.error("[Tier1] pm_create_portfolio error:", error);
      toast.error("Failed to create client");
    } else {
      if (pendingLogoFile && rpcData) {
        try {
          const pathArgs: LogoPathArgs = {
            level: "portfolio",
            clientName: orgContext.clientName || "client",
            subOrgName: orgContext.subOrgName || "sub-org",
            companyName: createName.trim(),
            entityId: rpcData,
          };
          await uploadLogoForEntity(pendingLogoFile, pathArgs);
        } catch (logoErr) {
          console.error("[Tier1] logo upload failed:", logoErr);
          toast.error("Client created but logo upload failed");
        }
      }
      toast.success("Client created");
      setShowCreateModal(false);
      setCreateName("");
      setSelectedQuestionnaire(availableAssessments[0]?.questionnaire_key ?? "");
      setPendingLogoFile(null);
      await refresh();
    }
    setCreating(false);
  };

  if (loading) {
    return (
      <AppLayout title="Portfolio Manager">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (!hasSubOrg) {
    return (
      <AppLayout title="Portfolio Manager">
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Sub-Organizations Assigned</h2>
              <p className="text-muted-foreground text-center">
                You are not assigned as a portfolio manager to any sub-organizations.
                <br />
                Please contact your administrator.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const companies = data?.companies ?? [];
  const atLimit = companies.length >= MAX_TIER1_CLIENTS;

  return (
    <AppLayout title="Portfolio Manager">
      <div className="container mx-auto py-8 space-y-6">
        {/* Org context header */}
        {(orgContext.clientName || orgContext.subOrgName) && (
          <div className="flex items-center gap-3 text-sm">
            <BrandedLogo
              src={getLogoUrl(orgContext.subOrgLogo) || getLogoUrl(orgContext.clientLogo)}
              alt={orgContext.subOrgName || orgContext.clientName || "Client"}
              className="h-10 w-auto"
              fallback={<Building2 className="h-5 w-5 text-muted-foreground" />}
            />
            <span className="font-semibold text-foreground">
              {orgContext.subOrgName || orgContext.clientName}
            </span>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">My Clients</h1>
            <p className="text-muted-foreground">
              Create a client, run an assessment, and generate a report.
            </p>
          </div>
          <Button
            onClick={openCreateModal}
            disabled={atLimit}
            title={atLimit ? `Tier 1 allows up to ${MAX_TIER1_CLIENTS} clients` : undefined}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Client
          </Button>
        </div>

        {/* Limit banner */}
        {atLimit && (
          <div className="flex items-center justify-between rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-950/30 dark:border-yellow-700 px-4 py-3 text-sm">
            <span className="text-yellow-800 dark:text-yellow-200">
              You've reached the Tier 1 limit of {MAX_TIER1_CLIENTS} clients.
            </span>
            <Button variant="link" size="sm" className="text-yellow-700 dark:text-yellow-300 px-0 h-auto font-semibold">
              Upgrade Plan →
            </Button>
          </div>
        )}

        {/* Client cards */}
        {loadingDashboard ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tier1ClientCards companies={companies} onRefresh={refresh} />
        )}

        {/* Locked feature hints */}
        <div className="grid gap-3 md:grid-cols-3 mt-2">
          <TierGate feature="Portfolio Analytics" />
          <TierGate feature="Benchmarking" />
          <TierGate feature="Cross-Client Comparison" />
        </div>
      </div>

      {/* Create Client modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Client Name</Label>
              <Input
                id="name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="Enter client name"
              />
            </div>
            <LogoUpload
              onUploaded={() => {}}
              onFileSelected={(file) => setPendingLogoFile(file)}
            />
            <div className="space-y-2">
              <Label>Category</Label>
              <p className="text-sm font-medium py-2 px-3 bg-muted rounded-md text-muted-foreground">
                {TIER1_MSP_CATEGORY}
              </p>
              <p className="text-xs text-muted-foreground">
                Your plan includes assessments from this category.{" "}
                <span className="text-primary cursor-pointer hover:underline">Upgrade</span> to access additional categories.
              </p>
            </div>
            <div className="space-y-2">
              <Label>Assessment</Label>
              <Select
                value={selectedQuestionnaire}
                onValueChange={setSelectedQuestionnaire}
                disabled={loadingAssessments}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={loadingAssessments ? "Loading assessments..." : "Select assessment"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {availableAssessments.map((row) => (
                    <SelectItem key={row.questionnaire_key} value={row.questionnaire_key}>
                      {getQuestionnaireDisplayTitle(
                        row.questionnaire_key,
                        row.q?.meta as Record<string, unknown>,
                      )}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating || !createName.trim() || !selectedQuestionnaire}
            >
              {creating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                "Create"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
